"use client";

import { useState } from "react";
import { Music, User, Phone, Calendar, Target, Send, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

type FormData = {
  name: string;
  phone: string;
  age: string;
  goal: string;
};

type SubmitStatus = "idle" | "success" | "error";

/* ─────────────────────────────────────────
   FIELD CONFIG
───────────────────────────────────────── */

const FIELDS = [
  {
    id: "name",
    label: "Họ và Tên",
    icon: User,
    type: "text",
    placeholder: "Nguyễn Văn A",
    inputMode: undefined as undefined,
  },
  {
    id: "phone",
    label: "Số Điện Thoại",
    icon: Phone,
    type: "tel",
    placeholder: "0912 345 678",
    inputMode: "tel" as React.HTMLAttributes<HTMLInputElement>["inputMode"],
  },
  {
    id: "age",
    label: "Tuổi",
    icon: Calendar,
    type: "number",
    placeholder: "25",
    inputMode: "numeric" as React.HTMLAttributes<HTMLInputElement>["inputMode"],
  },
];

const BENEFITS = [
  "Lịch học linh hoạt theo giờ bạn rảnh",
  "Giáo viên phản hồi kỹ thuật từng buổi",
  "Học 1 kèm 1 – tiến bộ nhanh, đúng kỹ thuật",
];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const DangKyHocGuitar = () => {
  const [formData, setFormData] = useState<FormData>({ name: "", phone: "", age: "", goal: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [focused, setFocused] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    try {
      const res = await fetch("/api/discord", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", phone: "", age: "", goal: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.phone && formData.age && formData.goal;

  return (
    <div
      className="min-h-screen py-16 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "var(--background)", fontFamily: "var(--font-roboto)" }}
    >
      <div className="max-w-5xl mx-auto">

        {/* ── HEADER ── */}
        <header className="text-center mb-14">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                backgroundColor: "rgba(200,169,110,0.1)",
                border: "1.5px solid rgba(200,169,110,0.3)",
                boxShadow: "0 0 40px rgba(200,169,110,0.12)",
              }}
            >
              <Music className="w-9 h-9" style={{ color: "var(--brand-primary)" }} />
            </div>
          </div>

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-5 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            Khóa Học Guitar Tại – Quận 12
          </div>

          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4 tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Đăng Ký{" "}
            <span
              className="relative inline-block"
              style={{ color: "var(--brand-primary)" }}
            >
              Học Guitar
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
          </h1>

          <p
            className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
            style={{ color: "var(--muted-foreground)" }}
          >
            Điền thông tin để được tư vấn lộ trình học phù hợp — hoàn toàn miễn phí, không áp lực.
          </p>

          {/* Divider */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
          </div>
        </header>

        {/* ── MAIN GRID ── */}
        <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-10 items-start">

          {/* ── FORM CARD ── */}
          <div
            className="rounded-3xl border overflow-hidden mb-8 lg:mb-0"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "rgba(200,169,110,0.25)",
              boxShadow: "0 4px 40px rgba(200,169,110,0.07), 0 0 0 1px rgba(200,169,110,0.06)",
            }}
          >
            {/* Gold top bar */}
            <div className="h-1 w-full" style={{ backgroundColor: "var(--brand-primary)" }} />

            <div className="p-7 sm:p-10">
              {/* Success state */}
              {submitStatus === "success" ? (
                <div className="py-10 flex flex-col items-center text-center gap-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "rgba(200,169,110,0.12)", border: "1.5px solid rgba(200,169,110,0.3)" }}
                  >
                    <CheckCircle2 className="w-8 h-8" style={{ color: "var(--brand-primary)" }} />
                  </div>
                  <h3
                    className="text-xl font-black"
                    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                  >
                    Đăng ký thành công!
                  </h3>
                  <p className="text-sm leading-relaxed max-w-sm" style={{ color: "var(--muted-foreground)" }}>
                    Chúng tôi đã nhận được thông tin của bạn. Giáo viên sẽ liên hệ trong vòng{" "}
                    <span style={{ color: "var(--brand-primary)", fontWeight: 600 }}>24 giờ</span> để sắp xếp buổi học thử.
                  </p>
                  <button
                    onClick={() => setSubmitStatus("idle")}
                    className="mt-2 text-sm font-semibold underline underline-offset-2"
                    style={{ color: "var(--brand-primary)" }}
                  >
                    Gửi thêm đăng ký khác
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">

                  {/* Text fields */}
                  {FIELDS.map(({ id, label, icon: Icon, type, placeholder, inputMode }) => (
                    <div key={id}>
                      <label
                        htmlFor={id}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2.5"
                        style={{
                          fontFamily: "var(--font-montserrat)",
                          color: focused === id ? "var(--brand-primary)" : "var(--muted-foreground)",
                          transition: "color 0.2s",
                        }}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type={type}
                        id={id}
                        name={id}
                        value={formData[id as keyof FormData]}
                        onChange={handleChange}
                        onFocus={() => setFocused(id)}
                        onBlur={() => setFocused(null)}
                        required
                        placeholder={placeholder}
                        inputMode={inputMode}
                        min={id === "age" ? 5 : undefined}
                        max={id === "age" ? 100 : undefined}
                        className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all duration-200"
                        style={{
                          backgroundColor: "var(--background)",
                          color: "var(--foreground)",
                          border: focused === id
                            ? "1.5px solid rgba(200,169,110,0.7)"
                            : "1.5px solid var(--border)",
                          boxShadow: focused === id ? "0 0 0 3px rgba(200,169,110,0.1)" : "none",
                          fontFamily: "var(--font-roboto)",
                        }}
                      />
                    </div>
                  ))}

                  {/* Goal textarea */}
                  <div>
                    <label
                      htmlFor="goal"
                      className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2.5"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        color: focused === "goal" ? "var(--brand-primary)" : "var(--muted-foreground)",
                        transition: "color 0.2s",
                      }}
                    >
                      <Target className="w-3.5 h-3.5" />
                      Mục Tiêu Học Guitar
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <textarea
                      id="goal"
                      name="goal"
                      value={formData.goal}
                      onChange={handleChange}
                      onFocus={() => setFocused("goal")}
                      onBlur={() => setFocused(null)}
                      required
                      rows={4}
                      placeholder="Ví dụ: Mình học Guitar được : (bao lâu) và mong muốn mình sẽ phát triển những gì"
                      className="w-full px-4 py-3.5 rounded-xl text-sm outline-none resize-none transition-all duration-200"
                      style={{
                        backgroundColor: "var(--background)",
                        color: "var(--foreground)",
                        border: focused === "goal"
                          ? "1.5px solid rgba(200,169,110,0.7)"
                          : "1.5px solid var(--border)",
                        boxShadow: focused === "goal" ? "0 0 0 3px rgba(200,169,110,0.1)" : "none",
                        fontFamily: "var(--font-roboto)",
                      }}
                    />
                  </div>

                  {/* Error message */}
                  {submitStatus === "error" && (
                    <div
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl border"
                      style={{
                        backgroundColor: "rgba(239,68,68,0.08)",
                        borderColor: "rgba(239,68,68,0.3)",
                      }}
                    >
                      <AlertCircle className="w-4 h-4 shrink-0" style={{ color: "#ef4444" }} />
                      <p className="text-sm" style={{ color: "#ef4444" }}>
                        Có lỗi xảy ra. Vui lòng thử lại hoặc gọi{" "}
                        <a href="tel:0325805893" className="font-bold underline underline-offset-2">
                          0325 805 893
                        </a>
                      </p>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={!isFormValid || isSubmitting}
                    className="group w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm transition-all duration-200 active:scale-[0.98]"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: isFormValid && !isSubmitting
                        ? "var(--brand-primary)"
                        : "var(--border)",
                      color: isFormValid && !isSubmitting
                        ? "var(--brand-primary-foreground)"
                        : "var(--muted-foreground)",
                      boxShadow: isFormValid && !isSubmitting
                        ? "0 4px 20px rgba(200,169,110,0.3)"
                        : "none",
                      cursor: isFormValid && !isSubmitting ? "pointer" : "not-allowed",
                      opacity: isFormValid && !isSubmitting ? 1 : 0.6,
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div
                          className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: "var(--brand-primary-foreground)", borderTopColor: "transparent" }}
                        />
                        Đang gửi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Gửi Đăng Ký Ngay
                        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  <p
                    className="text-xs text-center"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Thông tin của bạn được bảo mật hoàn toàn · Không spam
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="space-y-5 lg:sticky lg:top-8">

            {/* Benefits card */}
            {/* <div
              className="rounded-3xl p-7 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.25)",
                boxShadow: "0 0 30px rgba(200,169,110,0.05)",
              }}
            >
              <div
                className="h-1 w-10 rounded-full mb-5"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />
              <p
                className="text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Bạn sẽ nhận được
              </p>
              <ul className="space-y-3">
                {BENEFITS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div
                      className="shrink-0 w-5 h-5 mt-0.5 rounded-full flex items-center justify-center text-xs font-black"
                      style={{
                        backgroundColor: "rgba(200,169,110,0.12)",
                        color: "var(--brand-primary)",
                        border: "1.5px solid rgba(200,169,110,0.3)",
                      }}
                    >
                      ✓
                    </div>
                    <span className="text-sm leading-relaxed" style={{ color: "var(--foreground)" }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div> */}

            {/* Contact card */}
            <div
              className="rounded-3xl p-7 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: "var(--muted-foreground)", fontFamily: "var(--font-montserrat)" }}
              >
                Liên hệ trực tiếp
              </p>

              <a
                href="tel:0325805893"
                className="group flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.1)",
                    border: "1.5px solid rgba(200,169,110,0.25)",
                  }}
                >
                  <Phone className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Hotline</p>
                  <p
                    className="font-bold text-sm"
                    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                  >
                    0325 805 893
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.1)",
                    border: "1.5px solid rgba(200,169,110,0.25)",
                  }}
                >
                  <Music className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Email</p>
                  <p
                    className="font-bold text-sm"
                    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                  >
                    tuilanguyencuong@gmail.com
                  </p>
                </div>
              </div>

              <div
                className="mt-5 pt-5 flex items-center gap-2 border-t"
                style={{ borderColor: "var(--border)" }}
              >
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: "#22c55e" }}
                />
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Mở cửa 8:00 – 20:00 · Thứ 2 – Chủ nhật
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DangKyHocGuitar;